import {
  FormErrors,
  FormValues,
  SUCCESS_CHAMPION_PREFERENCES,
} from '../types/ChampionApplicationFormTypes';
import { parseBirthDateToLocal } from './birthDateUtils';

// Common error messages (exact literals)
const ERROR_REQUIRED = 'This field is required.';
const ERROR_EMAIL = 'Enter a valid email address, such as name@email.com.';
const ERROR_MIN_LEN_4 = 'Length is too short - please enter at least 4 characters';
const ERROR_COMPLETE_FIELD = 'Complete this field.';
const ERROR_SELECT_ONE = 'Select at least one option.';
const ERROR_MIN_150 = 'Enter at least 150 characters.';
const ERROR_MAX_300 = 'Enter less than 300 characters.';

/** Max length for whyBecomeChampion, howContributeOnline, whyToCreateContent (must match TextArea maxLength) */
export const MAX_CHAMPION_ESSAY_LENGTH = 300;
const ERROR_ORG_ACCOUNT_REQUIRED = 'Please use an org account to sign up as success champion.';
const ERROR_BIRTH_DATE_AGE_21 = 'Community Leader Applicants must be 21 years or older.';
const ERROR_STREET_PO_BOX = "Enter a street address – we can't send swag to a PO box.";
const ERROR_INVALID_URL = 'Enter a valid URL.';
const ERROR_ACE_EXPERIENCE_MAX = 'Enter less than 32,768 characters.';
const ERROR_ACTIVITY_AND_EVENT_MAX = 'Enter less than 255 characters.';
const ERROR_OTHER_EXCELLENCE_MAX = 'Enter less than 100 characters.';
const ERROR_REFERRER_NAME_MAX = 'Enter less than 80 characters.';
const ERROR_PLEASE_SPECIFY_MAX = 'Enter less than 255 characters.';
const ERROR_STREET_MAX = 'Enter no more than 50 characters.';
const ERROR_CITY_MAX = 'Enter no more than 80 characters.';
const ERROR_STATE_PROVINCE_MAX = 'Enter no more than 20 characters.';
const ERROR_POSTAL_CODE_MAX = 'Enter no more than 10 characters.';

/** Champion signup / Salesforce limits — must match PersonalInfoSection prefill + Textfield maxLength */
export const MAX_CHAMPION_FIRST_NAME_LENGTH = 38;
export const MAX_CHAMPION_LAST_NAME_LENGTH = 40;

const ERROR_FIRST_NAME_MAX = `Enter no more than ${MAX_CHAMPION_FIRST_NAME_LENGTH} characters.`;
const ERROR_LAST_NAME_MAX = `Enter no more than ${MAX_CHAMPION_LAST_NAME_LENGTH} characters.`;

const MAX_EMAIL_LENGTH = 254;
const MAX_URL_LENGTH = 255;
const MAX_STREET_LENGTH = 50;
const MAX_CITY_LENGTH = 80;
const MAX_STATE_PROVINCE_LENGTH = 20;
const MAX_POSTAL_CODE_LENGTH = 10;
const MAX_ACE_EXPERIENCE_LENGTH = 32768;
const MAX_ACTIVITY_AND_EVENT_LENGTH = 255;
const MAX_OTHER_EXCELLENCE_LENGTH = 100;
const MAX_REFERRER_NAME_LENGTH = 80;
const MAX_PLEASE_SPECIFY_LENGTH = 255;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Matches common PO Box patterns: PO Box, P.O. Box, P O Box, Post Office Box, Postal Box, POBox, etc. */
const PO_BOX_PATTERN =
  /\b(po\s*\.?\s*o\.?\s*box|p\s+o\s+box|post\s*[-]?\s*office\s+box|postal\s+box|p\.?o\.?\s*box|post\s*office\s*box)\b/i;

function containsPOBox(value: string): boolean {
  return PO_BOX_PATTERN.test(value.trim());
}

function isValidUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_URL_LENGTH) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isAtLeast21(birthDate: Date): boolean {
  const today = new Date();
  const cutoff = new Date(today.getFullYear() - 21, today.getMonth(), today.getDate());
  return birthDate <= cutoff;
}

type ValidationOptions = {
  accountIsManaged: boolean;
};

export const validateChampionApplication = (values: FormValues, options: ValidationOptions): FormErrors => {
  const errors: FormErrors = {};
  const minLen = 4;

  // 1. Personal info + contact
  if (!values.firstName.trim()) {
    errors.firstName = ERROR_REQUIRED;
  } else if (values.firstName.length > MAX_CHAMPION_FIRST_NAME_LENGTH) {
    errors.firstName = ERROR_FIRST_NAME_MAX;
  }

  if (!values.lastName.trim()) {
    errors.lastName = ERROR_REQUIRED;
  } else if (values.lastName.length > MAX_CHAMPION_LAST_NAME_LENGTH) {
    errors.lastName = ERROR_LAST_NAME_MAX;
  }

  if (!values.email.trim() || values.email.trim().length > MAX_EMAIL_LENGTH || !EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = ERROR_EMAIL;
  }

  if (!values.communityProfileUrl.trim()) {
    errors.communityProfileUrl = ERROR_REQUIRED;
  }

  if (!values.linkedinUrl.trim()) {
    errors.linkedinUrl = ERROR_REQUIRED;
  } else if (!isValidUrl(values.linkedinUrl)) {
    errors.linkedinUrl = ERROR_INVALID_URL;
  }

  // 2. Address
  if (!values.street.trim()) {
    errors.street = ERROR_REQUIRED;
  } else if (values.street.trim().length < minLen) {
    errors.street = ERROR_MIN_LEN_4;
  } else if (values.street.length > MAX_STREET_LENGTH) {
    errors.street = ERROR_STREET_MAX;
  } else if (containsPOBox(values.street)) {
    errors.street = ERROR_STREET_PO_BOX;
  }

  if (!values.city.trim()) {
    errors.city = ERROR_REQUIRED;
  } else if (values.city.trim().length < minLen) {
    errors.city = ERROR_MIN_LEN_4;
  } else if (values.city.length > MAX_CITY_LENGTH) {
    errors.city = ERROR_CITY_MAX;
  }

  if (!values.stateProvince.trim()) {
    errors.stateProvince = ERROR_REQUIRED;
  } else if (values.stateProvince.trim().length < minLen) {
    errors.stateProvince = ERROR_MIN_LEN_4;
  } else if (values.stateProvince.length > MAX_STATE_PROVINCE_LENGTH) {
    errors.stateProvince = ERROR_STATE_PROVINCE_MAX;
  }

  if (!values.postalCode.trim()) {
    errors.postalCode = ERROR_REQUIRED;
  } else if (values.postalCode.trim().length < minLen) {
    errors.postalCode = ERROR_MIN_LEN_4;
  } else if (values.postalCode.length > MAX_POSTAL_CODE_LENGTH) {
    errors.postalCode = ERROR_POSTAL_CODE_MAX;
  }

  if (!values.country.trim()) {
    errors.country = ERROR_COMPLETE_FIELD;
  }

  // Birth date: optional, but if provided must be 21+ years old
  if (values.birthDate.trim()) {
    const parsed = parseBirthDateToLocal(values.birthDate);
    if (parsed && !isAtLeast21(parsed)) {
      errors.birthDate = ERROR_BIRTH_DATE_AGE_21;
    }
  }

  // 3. Work & partner
  if (values.partnerType.length === 0) {
    errors.partnerType = ERROR_SELECT_ONE;
  }

  // 4. Products used
  if (values.products.length === 0) {
    errors.products = ERROR_SELECT_ONE;
  }
  if (values.products.includes('other') && !values.otherAtlassianProducts.trim()) {
    errors.otherAtlassianProducts = ERROR_REQUIRED;
  } else if (values.products.includes('other') && values.otherAtlassianProducts.length > MAX_PLEASE_SPECIFY_LENGTH) {
    errors.otherAtlassianProducts = ERROR_PLEASE_SPECIFY_MAX;
  }

  // 5. Global motivation: 150–300 chars
  const why = values.whyBecomeChampion.trim();
  if (!why) {
    errors.whyBecomeChampion = ERROR_REQUIRED;
  } else if (why.length < 150) {
    errors.whyBecomeChampion = ERROR_MIN_150;
  } else if (why.length > MAX_CHAMPION_ESSAY_LENGTH) {
    errors.whyBecomeChampion = ERROR_MAX_300;
  }

  // 6. Top preference
  if (!values.championPreference) {
    errors.championPreference = ERROR_SELECT_ONE;
  }

  if (
    values.championPreference &&
    SUCCESS_CHAMPION_PREFERENCES.includes(values.championPreference) &&
    !options.accountIsManaged
  ) {
    errors.championPreference = ERROR_ORG_ACCOUNT_REQUIRED;
  }

  // 6a. Community Forums
  if (values.championPreference === 'Community Forums Contributor') {
    const txt = values.howContributeOnline.trim();
    if (!txt) {
      errors.howContributeOnline = ERROR_REQUIRED;
    } else if (txt.length < 150) {
      errors.howContributeOnline = ERROR_MIN_150;
    } else if (txt.length > MAX_CHAMPION_ESSAY_LENGTH) {
      errors.howContributeOnline = ERROR_MAX_300;
    }
  }

  // 6b. Community Events
  if (values.championPreference === 'Community Event Organizer') {
    if (!values.attendedAce) {
      errors.attendedAce = ERROR_REQUIRED;
    }
    if (values.attendedAce === 'yes') {
      if (!values.aceExperience.trim()) {
        errors.aceExperience = ERROR_REQUIRED;
      } else if (values.aceExperience.length > MAX_ACE_EXPERIENCE_LENGTH) {
        errors.aceExperience = ERROR_ACE_EXPERIENCE_MAX;
      }
    }
    if (!values.fourEventsPerYear) {
      errors.fourEventsPerYear = ERROR_REQUIRED;
    }
    if (!values.activityAndEvent.trim()) {
      errors.activityAndEvent = ERROR_REQUIRED;
    } else if (values.activityAndEvent.length > MAX_ACTIVITY_AND_EVENT_LENGTH) {
      errors.activityAndEvent = ERROR_ACTIVITY_AND_EVENT_MAX;
    }
  }

  // 6c. Content Creator
  if (values.championPreference === 'Creator') {
    const whyContent = values.whyToCreateContent.trim();
    if (!whyContent) {
      errors.whyToCreateContent = ERROR_REQUIRED;
    } else if (whyContent.length < 150) {
      errors.whyToCreateContent = ERROR_MIN_150;
    } else if (whyContent.length > MAX_CHAMPION_ESSAY_LENGTH) {
      errors.whyToCreateContent = ERROR_MAX_300;
    }

    if (!values.contentSharingFrequency) {
      errors.contentSharingFrequency = ERROR_REQUIRED;
    }

    if (!values.bestContent1.trim()) {
      errors.bestContent1 = ERROR_REQUIRED;
    } else if (!isValidUrl(values.bestContent1)) {
      errors.bestContent1 = ERROR_INVALID_URL;
    }
    if (!values.bestContent2.trim()) {
      errors.bestContent2 = ERROR_REQUIRED;
    } else if (!isValidUrl(values.bestContent2)) {
      errors.bestContent2 = ERROR_INVALID_URL;
    }
    if (!values.bestContent3.trim()) {
      errors.bestContent3 = ERROR_REQUIRED;
    } else if (!isValidUrl(values.bestContent3)) {
      errors.bestContent3 = ERROR_INVALID_URL;
    }

    if (!values.primarySocialProfile.trim()) {
      errors.primarySocialProfile = ERROR_REQUIRED;
    } else if (!isValidUrl(values.primarySocialProfile)) {
      errors.primarySocialProfile = ERROR_INVALID_URL;
    }

    if (!values.commitSharingContent) {
      errors.commitSharingContent = ERROR_REQUIRED;
    }

    if (values.otherURL.trim() && !isValidUrl(values.otherURL)) {
      errors.otherURL = ERROR_INVALID_URL;
    }
  }

  // 6d. Training Builder
  if (values.championPreference === 'Trainer/Mentor') {
    if (!values.trainingFrequency) {
      errors.trainingFrequency = ERROR_REQUIRED;
    }
    if (values.trainingSources.length === 0) {
      errors.trainingSources = ERROR_SELECT_ONE;
    }
  }

  // 6e. CUG Leader
  if (values.championPreference === 'CUG Leader') {
    if (!values.activeCug) {
      errors.activeCug = ERROR_REQUIRED;
    }
    if (!values.eventsPerYear) {
      errors.eventsPerYear = ERROR_REQUIRED;
    }
  }

  // 6f. CoE Contributor
  if (values.championPreference === 'CoE Member') {
    if (!values.discussInCoE) {
      errors.discussInCoE = ERROR_REQUIRED;
    }
    if (!values.coeFocus) {
      errors.coeFocus = ERROR_REQUIRED;
    }
    if (values.coeFocus === 'Other') {
      if (!values.otherExcellence.trim()) {
        errors.otherExcellence = ERROR_REQUIRED;
      } else if (values.otherExcellence.length > MAX_OTHER_EXCELLENCE_LENGTH) {
        errors.otherExcellence = ERROR_OTHER_EXCELLENCE_MAX;
      }
    }
  }

  // 7. How did you find out about the Atlassian Champions program?
  if (!values.howHeard) {
    errors.howHeard = ERROR_SELECT_ONE;
  }
  if (values.howHeard === 'Other') {
    if (!values.howHeardOther.trim()) {
      errors.howHeardOther = ERROR_COMPLETE_FIELD;
    } else if (values.howHeardOther.length > MAX_PLEASE_SPECIFY_LENGTH) {
      errors.howHeardOther = ERROR_PLEASE_SPECIFY_MAX;
    }
  }
  if (values.howHeard === 'Atlassian Employee (Other than EA or TAM)') {
    if (!values.howHeardEmployee.trim()) {
      errors.howHeardEmployee = ERROR_COMPLETE_FIELD;
    } else if (values.howHeardEmployee.length > MAX_PLEASE_SPECIFY_LENGTH) {
      errors.howHeardEmployee = ERROR_PLEASE_SPECIFY_MAX;
    }
  }
  if (values.howHeard === 'Atlassian Community Leader') {
    if (!values.referrerName.trim()) {
      errors.referrerName = ERROR_COMPLETE_FIELD;
    } else if (values.referrerName.length > MAX_REFERRER_NAME_LENGTH) {
      errors.referrerName = ERROR_REFERRER_NAME_MAX;
    }
  }

  // 8. Agreements (program + privacy + NDA always; 4th when Success Champion track selected)
  if (!values.consentProgramAgreement) {
    errors.consentProgramAgreement = ERROR_COMPLETE_FIELD;
  }
  if (!values.consentPrivacyPolicy) {
    errors.consentPrivacyPolicy = ERROR_COMPLETE_FIELD;
  }
  if (!values.consentNda) {
    errors.consentNda = ERROR_COMPLETE_FIELD;
  }

  const needsSuccessChampionDataAgreement =
    values.championPreference !== null &&
    SUCCESS_CHAMPION_PREFERENCES.includes(values.championPreference);
  if (needsSuccessChampionDataAgreement && !values.consentSuccessChampionAgreement) {
    errors.consentSuccessChampionAgreement = ERROR_COMPLETE_FIELD;
  }

  return errors;
};
